<template>
  <SharedHeader
    theme="public"
    audience="angler"
    :links="publicLinks"
    always-visible
    :show-default-actions="false"
  >
    <template #logo>
      <router-link
        :to="logoRoute"
        class="public-header__brand"
        :aria-label="organizationName || t('public.header.home')"
      >
        <img
          v-if="organizationLogo && !logoFailed"
          :src="organizationLogo"
          :alt="organizationName"
          class="public-header__logo"
          @error="handleLogoError"
        >
        <img
          v-else
          src="/images/logo/logo-wobg.png"
          alt="Mylimni"
          class="public-header__logo public-header__logo--fallback"
        >
        <span
          v-if="organizationName && !organizationLogo"
          class="public-header__org-name"
        >
          {{ organizationName }}
        </span>
      </router-link>
    </template>
  </SharedHeader>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SharedHeader from '@/components/shared/SharedHeader.vue'
import type { HeaderLink } from '@/composables/useHeaderConfig'

const props = defineProps<{
  orgSlug?: string
  organizationName?: string
  organizationLogo?: string
  contactEmail?: string
  contactPhone?: string
}>()

const { t } = useI18n()
const logoFailed = ref(false)

const logoRoute = computed(() =>
  props.orgSlug
    ? { name: 'public-lakes', params: { orgSlug: props.orgSlug } }
    : { name: 'public-lakes-marketplace' },
)

const publicLinks = computed<HeaderLink[]>(() => [
  {
    labelKey: 'public.header.lakes',
    to: props.orgSlug
      ? { name: 'public-lakes', params: { orgSlug: props.orgSlug } }
      : { name: 'public-lakes-marketplace' },
  },
  {
    labelKey: 'public.header.contact',
    id: 'contact',
  },
])

const handleLogoError = () => {
  logoFailed.value = true
}
</script>

<style scoped>
.public-header__brand {
  display: flex;
  align-items: center;
  gap: var(--public-space-3);
  text-decoration: none;
  color: var(--public-text-inverted);
}

.public-header__logo {
  height: 40px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
}

.public-header__logo--fallback {
  filter: brightness(0) invert(1);
}

.public-header__org-name {
  font-family: var(--public-font-family);
  font-size: var(--public-text-xl);
  font-weight: var(--public-font-bold);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.public-header__nav-link {
  font-family: var(--public-font-family);
  font-size: var(--public-text-sm);
  font-weight: var(--public-font-medium);
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  background: none;
  border: none;
  padding: var(--public-space-3) var(--public-space-4);
  border-radius: var(--public-radius-lg);
}

.public-header__nav-link:hover {
  opacity: 0.8;
}

.shared-header--sticky .public-header__brand,
.shared-header--always-visible .public-header__brand {
  color: var(--public-text-primary);
}

.shared-header--sticky .public-header__logo--fallback,
.shared-header--always-visible .public-header__logo--fallback {
  filter: none;
}

.public-header__mobile-link {
  font-family: var(--public-font-family);
  font-size: var(--public-text-base);
  font-weight: var(--public-font-medium);
  color: var(--public-text-primary);
  background: none;
  border: none;
  text-align: left;
  padding: var(--public-space-4);
}

.public-header__mobile-link:hover {
  opacity: 0.8;
}
</style>
